import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Input,
  Tag,
  TagLabel,
  Text,
  useToast,
  type SystemStyleObject,
} from "@chakra-ui/react";
import type { Content } from "@tiptap/react";
import { Editor } from "@tiptap/react";
import { components, OptionProps, Select } from "chakra-react-select";
import { t } from "i18next";
import { useCallback, useRef, useState } from "react";
import { FaFileImport, FaHighlighter, FaImage, FaLink, FaListOl, FaListUl, FaMagic, FaTable, FaTimes } from "react-icons/fa";
import { TextDocumentDto, useListDocumentTemplatesQuery } from "../../../autogen/bff-api";
import { useSub } from "../../subscription/useSub";
import { parseTiptapContent, TipTapCopyTemplateDocumentsHandler } from "./TipTap";

const rgbToHex = (rgb?: string) => {
  const regexRes = rgb?.match(/\d+/g)?.map(Number);
  if (!regexRes) return;
  const [r, g, b] = regexRes;
  if (!(isFinite(r) && isFinite(g) && isFinite(b))) return;
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
};

const replaceUploadIdsAndSrcInContent = ({
  content,
  uploadIdMapping,
  srcMapping,
}: {
  content: unknown;
  uploadIdMapping: Record<string, string>;
  srcMapping: Record<string, string>;
}): unknown => {
  if (!content || typeof content !== "object") return content;

  const node = content as Record<string, unknown>;

  if (node.type === "image" && node.attrs && typeof node.attrs === "object") {
    const attrs = node.attrs as Record<string, unknown>;
    const oldUploadId = attrs.uploadId as string | undefined;
    if (oldUploadId) {
      const newAttrs = { ...attrs };
      if (uploadIdMapping[oldUploadId]) {
        newAttrs.uploadId = uploadIdMapping[oldUploadId];
      }
      if (srcMapping[oldUploadId]) {
        newAttrs.src = srcMapping[oldUploadId];
      }
      return { ...node, attrs: newAttrs };
    }
  }

  if (Array.isArray(node.content)) {
    return {
      ...node,
      content: node.content.map((child) =>
        replaceUploadIdsAndSrcInContent({ content: child, uploadIdMapping, srcMapping })
      ),
    };
  }

  return node;
};

export interface ToolbarConfig {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  headings?: boolean;
  colors?: boolean;
  lists?: boolean;
  link?: boolean;
  table?: boolean;
  image?: boolean;
  template?: boolean;
  autofill?: boolean;
}

export const MenuBar = ({
  editor,
  onImageSelect,
  onCopyTemplateDocuments,
  onAutofill,
  toolbar,
}: {
  editor?: Editor;
  onImageSelect?: (files: File[]) => void;
  onCopyTemplateDocuments?: TipTapCopyTemplateDocumentsHandler;
  onAutofill?: () => void;
  toolbar?: ToolbarConfig;
}) => {
  const toolbarConfig: Required<ToolbarConfig> = {
    bold: true, italic: true, underline: true,
    headings: true, colors: true, lists: true,
    link: true, table: true, image: true, template: true, autofill: true,
    ...toolbar,
  };

  type TemplateOption = { label: string; value: string; tags: string[] };

  const toast = useToast();

  const sub = useSub();

  const { data: documentTemplatesData, isLoading } = useListDocumentTemplatesQuery(undefined, {
    skip: !toolbarConfig.template,
  });

  const colorInputRef = useRef<HTMLInputElement>(null);
  const backgroundColorInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const [showTemplateSelector, setShowTemplateSelector] = useState(false);

  const triggerImageInput = () => {
    imageInputRef.current?.click();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && onImageSelect) {
      onImageSelect(Array.from(files));
    }
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  const triggerColorInput = () => {
    colorInputRef.current?.click();
  };

  const triggerBackgroundColorInput = () => {
    backgroundColorInputRef.current?.click();
  };

  const handleTemplateSelect = useCallback(
    async (template: TextDocumentDto) => {
      if (!editor) return;

      const content = parseTiptapContent(template.text);
      const hasDocumentsToCopy = onCopyTemplateDocuments && template.documents.length > 0;

      if (hasDocumentsToCopy) {
        try {
          const { uploadIdMapping, srcMapping } = await onCopyTemplateDocuments({
            templateDocuments: template.documents,
          });
          const updatedContent = replaceUploadIdsAndSrcInContent({ content, uploadIdMapping, srcMapping }) as Content;
          editor.commands.insertContent(updatedContent);
        } catch (error) {
          toast({
            variant: "subtle",
            status: "error",
            title: t("Failed to insert template"),
            description: t("Could not copy template documents"),
          });
          return;
        }
      } else {
        editor.commands.insertContent(content);
      }

      toast({
        variant: "subtle",
        status: "success",
        title: t("Content inserted"),
        description: `${t("Inserted content from the template called")} ${template.name}`,
      });
      setShowTemplateSelector(false);
    },
    [editor, onCopyTemplateDocuments, toast]
  );

  const setLink = useCallback(() => {
    if (!editor) return;

    if (editor.isActive("link")) {
      editor.chain().focus().unsetLink().run();
      return;
    }

    const protocol = "https://";

    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", !previousUrl ? protocol : previousUrl);

    if (url === null) return;

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url.startsWith(protocol) ? url : protocol + url })
      .run();
  }, [editor]);

  if (!editor) return null;

  if (showTemplateSelector)
    return (
      <Flex width="full">
        <Select<TemplateOption>
          unstyled
          placeholder={`${t("Select document template")}...`}
          isLoading={isLoading}
          options={documentTemplatesData?.documents.map((doc) => ({
            label: doc.name ? doc.name : (t("No title") ?? ""),
            value: doc.id,
            tags: doc.tags,
          }))}
          filterOption={(option: { label: string; data: TemplateOption }, inputValue: string) => {
            const labelMatch = option.label.toLowerCase().includes(inputValue.toLowerCase());
            const tagsMatch = option.data.tags.some((tag) => tag.toLowerCase().includes(inputValue.toLowerCase()));
            return labelMatch || tagsMatch;
          }}
          chakraStyles={{
            container: (provided: SystemStyleObject) => ({
              ...provided,
              width: "full",
            }),
            control: (provided: SystemStyleObject) => ({
              ...provided,
              rounded: "md",
              roundedBottom: "none",
              roundedRight: "none",
              borderTop: "none",
              borderLeft: "none",
              borderRight: "none",
              _focus: { border: "none" },
            }),
            menu: (provided: SystemStyleObject) => ({ ...provided, rounded: "md" }),
            menuList: (provided: SystemStyleObject) => ({ ...provided, rounded: "md" }),
          }}
          components={{ Option: CustomOption }}
          onChange={(option: TemplateOption | null) => {
            const template = documentTemplatesData?.documents.find((d) => d.id === option?.value);
            if (template) {
              handleTemplateSelect(template);
            }
          }}
        />
        <IconButton
          aria-label="close template selector"
          icon={<Icon as={FaTimes} />}
          roundedLeft="0"
          roundedBottom="0"
          onClick={() => setShowTemplateSelector(false)}
        />
      </Flex>
    );

  return (
    <Flex columnGap="1" rowGap="1" flexWrap="wrap" justifyContent="center" py="2">
      {toolbarConfig.bold && (
        <Button
          size="xs"
          onClick={() => editor.chain().focus().toggleBold().run()}
          backgroundColor={editor.isActive("bold") ? "smBackgroundTertiary" : ""}
          fontWeight={"bold"}
        >
          {t("B")}
        </Button>
      )}
      {toolbarConfig.italic && (
        <Button
          size="xs"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          backgroundColor={editor.isActive("italic") ? "smBackgroundTertiary" : ""}
          fontStyle={"italic"}
        >
          {t("I")}
        </Button>
      )}
      {toolbarConfig.underline && (
        <Button
          size="xs"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          backgroundColor={editor.isActive("underline") ? "smBackgroundTertiary" : ""}
          textDecoration={"underline"}
        >
          {t("U")}
        </Button>
      )}
      {toolbarConfig.headings && (
        <>
          <Button
            size="xs"
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            backgroundColor={editor.isActive("heading", { level: 1 }) ? "smBackgroundTertiary" : ""}
            fontSize={"sm"}
          >
            {t("H1")}
          </Button>
          <Button
            size="xs"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            backgroundColor={editor.isActive("heading", { level: 2 }) ? "smBackgroundTertiary" : ""}
            fontSize={"xs"}
          >
            {t("H2")}
          </Button>
          <Button
            size="xs"
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            backgroundColor={editor.isActive("heading", { level: 3 }) ? "smBackgroundTertiary" : ""}
            fontSize={"xx-small"}
          >
            {t("H3")}
          </Button>
        </>
      )}
      {toolbarConfig.colors && (
        <>
          <Box position="relative" width="6" minWidth="6">
            <Input
              ref={colorInputRef}
              tabIndex={-1}
              type="color"
              size="xs"
              width="6"
              opacity="0"
              position="absolute"
              border="none"
              p="0"
              value={rgbToHex(editor.getAttributes("textStyle").color) ?? "#000000"}
              onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
            />
            <Button
              border="1px solid"
              borderColor="smBorder"
              backgroundColor="smBackground"
              color={editor.getAttributes("textStyle").color ?? "inherit"}
              size="xs"
              position="absolute"
              top="0"
              rounded="full"
              p="0"
              onClick={triggerColorInput}
            >
              A
            </Button>
          </Box>
          <Box position="relative" width="6" minWidth="6">
            <Input
              ref={backgroundColorInputRef}
              tabIndex={-1}
              type="color"
              size="xs"
              width="6"
              opacity="0"
              position="absolute"
              border="none"
              p="0"
              value={editor.getAttributes("highlight").color ?? "#ffffff"}
              onChange={(e) => editor.chain().focus().setHighlight({ color: e.target.value }).run()}
            />
            <Button
              aria-label="bg-color"
              border="1px solid"
              borderColor="smBorder"
              backgroundColor={editor.getAttributes("highlight").color ?? "smBackground"}
              size="xs"
              position="absolute"
              top="0"
              rounded="full"
              cursor="pointer"
              p="1"
              onClick={triggerBackgroundColorInput}
            >
              <Icon as={FaHighlighter} />
            </Button>
          </Box>
        </>
      )}
      {toolbarConfig.lists && (
        <>
          <Button
            size="xs"
            aria-label="bullet-list"
            cursor="pointer"
            backgroundColor={editor.isActive("bulletList") ? "smBackgroundTertiary" : ""}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <Icon as={FaListUl} />
          </Button>
          <Button
            size="xs"
            aria-label="ordered-list"
            cursor="pointer"
            backgroundColor={editor.isActive("orderedList") ? "smBackgroundTertiary" : ""}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <Icon as={FaListOl} />
          </Button>
        </>
      )}
      {toolbarConfig.link && (
        <Button
          size="xs"
          onClick={setLink}
          backgroundColor={editor.isActive("link") ? "smBackgroundTertiary" : ""}
          fontSize={"xx-small"}
        >
          <Icon as={FaLink} />
        </Button>
      )}
      {toolbarConfig.table && (
        <Button
          size="xs"
          onClick={() => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          isDisabled={editor.isActive("table")}
          backgroundColor={editor.isActive("table") ? "smBackgroundTertiary" : ""}
          fontSize={"xx-small"}
        >
          <Icon as={FaTable} />
        </Button>
      )}
      {toolbarConfig.image && onImageSelect && (
        <>
          <Input
            ref={imageInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            display="none"
            onChange={handleImageChange}
          />
          <Button size="xs" onClick={triggerImageInput} fontSize={"xx-small"}>
            <Icon as={FaImage} />
          </Button>
        </>
      )}
      {toolbarConfig.table && editor.isActive("table") && (
        <Flex
          rowGap="1"
          flexDirection="column"
          overflow="visible"
          height="max-content"
          backgroundColor="smBackgroundSecondary"
          rounded="md"
          zIndex="2"
          shadow="sm"
          px="1"
          pb="1"
        >
          <Button
            zIndex="10"
            size="xs"
            minHeight="6"
            px="1"
            onClick={() => editor.chain().focus().addRowAfter().run()}
            fontSize={"xx-small"}
          >
            {t("Add row")}
          </Button>
          <Button
            zIndex="10"
            size="xs"
            minHeight="6"
            px="1"
            onClick={() => editor.chain().focus().addColumnAfter().run()}
            fontSize={"xx-small"}
          >
            {t("Add column")}
          </Button>
          <Button
            zIndex="10"
            size="xs"
            minHeight="6"
            px="1"
            onClick={() => editor.chain().focus().deleteRow().run()}
            fontSize={"xx-small"}
          >
            {t("Delete row")}
          </Button>
          <Button
            zIndex="10"
            size="xs"
            minHeight="6"
            px="1"
            onClick={() => editor.chain().focus().deleteColumn().run()}
            fontSize={"xx-small"}
          >
            {t("Delete column")}
          </Button>
          <Button
            zIndex="10"
            size="xs"
            minHeight="6"
            px="1"
            onClick={() => editor.chain().focus().deleteTable().run()}
            fontSize={"xx-small"}
          >
            {t("Delete table")}
          </Button>
        </Flex>
      )}
      {toolbarConfig.template && sub.hasDocumentTemplateAccess && (
        <Button
          size="xs"
          colorScheme="teal"
          onClick={() => setShowTemplateSelector(true)}
          fontSize={"xx-small"}
          leftIcon={<Icon as={FaFileImport} />}
        >
          {t("Insert template")}
        </Button>
      )}
      {toolbarConfig.autofill && sub.hasAiAccess && onAutofill && (
        <Button
          size="xs"
          colorScheme="teal"
          variant="outline"
          onClick={onAutofill}
          fontSize={"xx-small"}
          leftIcon={<Icon as={FaMagic} />}
        >
          {t("Autofill")}
        </Button>
      )}
    </Flex>
  );
};

const CustomOption = (props: OptionProps<{ label: string; value: string; tags: string[] }, false>) => {
  return (
    <components.Option {...props}>
      <Flex alignItems="center" _hover={{ backgroundColor: "smBackgroundSecondary", cursor: "pointer" }} p="2" m="0">
        <Text fontSize="sm" pr="1">
          {props.label}
        </Text>
        <Flex columnGap="1">
          {props.data.tags.map((tag) => (
            <Tag key={tag} size="sm" alignItems="center">
              <TagLabel>{tag}</TagLabel>
            </Tag>
          ))}
        </Flex>
      </Flex>
    </components.Option>
  );
};
